/**
 * Engines Hub — top-level category hub for /engines.
 * Cross-references content/engines/*.md
 *
 * Lists the 5 proprietary AI engines that compose the Perception X2 stack:
 *  /organic-simulation-engine, /engines/narrative-generation,
 *  /cognitive-resonance, /engines/echo-chamber, /autonomous-operations
 */

export const enginesHubMeta = {
  title: "Perception X2 Engines | Five Proprietary AI Platforms",
  description:
    "Five proprietary AI engines that compose the Perception X2 perception amplification stack: Organic Simulation, Narrative Generation, Cognitive Resonance, Echo Chamber Architecture, and Autonomous Operations.",
  canonical: "https://perception.ac/engines",
  slug: "engines",
};

export interface EnginesChild {
  code: string;
  title: string;
  description: string;
  href: string;
  icon: string;
  accent: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet" | "rose";
}

export const enginesChildren: EnginesChild[] = [
  {
    code: "OSE",
    title: "Organic Simulation Engine",
    description:
      "Agent-based narrative propagation modeling with 10K–1M+ concurrent personas across scale-free, small-world, and random network topologies.",
    href: "/organic-simulation-engine",
    icon: "Network",
    accent: "cyan",
  },
  {
    code: "NGE",
    title: "Narrative Generation Engine",
    description:
      "Transformer-based multi-modal narrative synthesis. LLM + diffusion pipelines producing 95–98% organic-authentic content at scale.",
    href: "/engines/narrative-generation",
    icon: "PenTool",
    accent: "purple",
  },
  {
    code: "CRE",
    title: "Cognitive Resonance Engine",
    description:
      "144-dimensional emotional and cognitive resonance mapping. Precision targeting far beyond the 5–10 dimensions of conventional platforms.",
    href: "/cognitive-resonance",
    icon: "Brain",
    accent: "crimson",
  },
  {
    code: "ECA",
    title: "Echo Chamber Architecture",
    description:
      "Topology-aware reinforcement routing. Optimizes narrative flow across network structures for organic-appearing amplification.",
    href: "/engines/echo-chamber",
    icon: "Radio",
    accent: "amber",
  },
  {
    code: "AOE",
    title: "Autonomous Operations Engine",
    description:
      "Closed-loop decision orchestration across the entire Perception X2 stack. Sub-hour deployment, machine-speed response, zero-touch scaling.",
    href: "/autonomous-operations",
    icon: "Bot",
    accent: "emerald",
  },
];

export interface EnginesStat {
  value: string;
  label: string;
  to: number;
  suffix?: string;
  decimals?: number;
  icon: string;
  accent: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet";
}

export const enginesStats: EnginesStat[] = [
  { value: "5", label: "Proprietary Engines", to: 5, icon: "Cpu", accent: "crimson" },
  { value: "1M+", label: "Concurrent Personas", to: 1, suffix: "M+", decimals: 0, icon: "Users", accent: "cyan" },
  { value: "144", label: "Resonance Dimensions", to: 144, icon: "Activity", accent: "purple" },
  { value: "98%", label: "Organic Authenticity", to: 98, suffix: "%", decimals: 0, icon: "ShieldCheck", accent: "emerald" },
  { value: "47m", label: "Median Deploy", to: 47, suffix: "m", decimals: 0, icon: "Zap", accent: "amber" },
];

export interface EnginesFeature {
  title: string;
  description: string;
  icon: string;
  accent: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet" | "rose";
}

export const enginesFeatures: EnginesFeature[] = [
  {
    title: "Agent-Based Modeling",
    description: "10K–1M+ concurrent simulated personas with calibrated cultural, demographic, and psychographic attributes.",
    icon: "Users",
    accent: "cyan",
  },
  {
    title: "Multi-Modal Generation",
    description: "Transformer LLMs + diffusion pipelines producing text, image, and short-form video content at machine speed.",
    icon: "Sparkles",
    accent: "purple",
  },
  {
    title: "144-D Cognitive Mapping",
    description: "Emotional and cognitive dimensions analyzed at 14× the depth of conventional sentiment platforms.",
    icon: "Brain",
    accent: "crimson",
  },
  {
    title: "Topology-Aware Routing",
    description: "Reinforcement flow optimization across scale-free, small-world, and random graph structures.",
    icon: "Share2",
    accent: "amber",
  },
  {
    title: "Closed-Loop Orchestration",
    description: "Continuous feedback between sensing, simulation, generation, and deployment. No manual handoffs.",
    icon: "Workflow",
    accent: "emerald",
  },
  {
    title: "Sub-Hour Operational",
    description: "From strategic sign-off to live engine orchestration in under 60 minutes. Crisis-class response windows.",
    icon: "Zap",
    accent: "denim",
  },
  {
    title: "Cross-Engine Composition",
    description: "Five engines orchestrated at runtime to match the strategic posture: stealth, moderate, aggressive, or maximum.",
    icon: "Layers",
    accent: "violet",
  },
  {
    title: "Defence-Grade Security",
    description: "AES-256, zero trust, SOC 2 Type II. Engine state and customer data isolated by cryptographic boundary.",
    icon: "ShieldCheck",
    accent: "rose",
  },
];

export interface EnginesUseCase {
  title: string;
  body: string;
  icon: string;
  accent: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet" | "rose";
}

export const enginesUseCases: EnginesUseCase[] = [
  {
    title: "Crisis Cascade Modeling",
    body: "Pre-simulate how a developing narrative will propagate across networks — then pre-position counter-messages before the cascade reaches critical mass.",
    icon: "Siren",
    accent: "crimson",
  },
  {
    title: "Multi-Modal Content Factories",
    body: "Run parallel narrative generation across text, image, and video modalities — each calibrated to platform-native voice and authenticity profile.",
    icon: "Sparkles",
    accent: "purple",
  },
  {
    title: "Precision Emotional Targeting",
    body: "Map audience cognition across 144 dimensions, then deploy narratives tuned to the precise resonance profile of each segment.",
    icon: "Target",
    accent: "cyan",
  },
  {
    title: "Organic Network Amplification",
    body: "Route narratives through topology-aware paths so amplification appears organic to platform algorithms and human reviewers alike.",
    icon: "Network",
    accent: "emerald",
  },
  {
    title: "Autonomous Decision Triggers",
    body: "Engines self-orchestrate on perception signals. No human in the loop for sub-critical decisions; human override only on strategic escalations.",
    icon: "Bot",
    accent: "amber",
  },
  {
    title: "Continuous Model Refinement",
    body: "Each engagement produces fresh training signal. Engines improve continuously — outcome quality compounds across deployments.",
    icon: "TrendingUp",
    accent: "denim",
  },
];

export interface EnginesWhy {
  title: string;
  body: string;
  metric?: string;
  accent: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet" | "rose";
}

export const enginesWhy: EnginesWhy[] = [
  {
    title: "Built In-House, Not Licensed",
    body: "Five engines, 15+ years of development, tested in the world's most demanding engagements. No white-label. No third-party model risk.",
    metric: "5",
    accent: "crimson",
  },
  {
    title: "Composed at Runtime",
    body: "Each engine handles a specific cognitive layer — they communicate through a typed orchestration bus and recompose based on the strategic posture.",
    metric: "5×",
    accent: "purple",
  },
  {
    title: "144-Dimensional Insight",
    body: "Cognitive Resonance Engine reads 144 emotional and cognitive dimensions — 14× the granularity of conventional sentiment platforms.",
    metric: "144D",
    accent: "cyan",
  },
  {
    title: "1M+ Persona Capacity",
    body: "Organic Simulation Engine operates up to 1,000,000 concurrent personas in a single propagation model — no other platform comes close.",
    metric: "1M+",
    accent: "emerald",
  },
  {
    title: "98% Organic Authenticity",
    body: "Narrative Generation Engine output is statistically indistinguishable from organic content by both algorithms and human reviewers.",
    metric: "98%",
    accent: "amber",
  },
  {
    title: "Sub-Hour Operational",
    body: "Autonomous Operations Engine brings the entire stack from sign-off to live orchestration in under 60 minutes.",
    metric: "<60m",
    accent: "denim",
  },
];

export interface EnginesFaq {
  q: string;
  a: string;
}

export const enginesFaq: EnginesFaq[] = [
  {
    q: "What are the five Perception X2 engines?",
    a: "The five proprietary engines are: Organic Simulation Engine (agent-based propagation modeling), Narrative Generation Engine (multi-modal content synthesis), Cognitive Resonance Engine (144-dimensional emotional mapping), Echo Chamber Architecture (topology-aware routing), and Autonomous Operations Engine (closed-loop orchestration).",
  },
  {
    q: "Why five engines instead of one large model?",
    a: "Specialized engines outperform monolithic models on specific cognitive tasks. Each engine is purpose-built for one layer — propagation modeling, content generation, emotional resonance, topology routing, or operational orchestration — and composed at runtime. This is the same architectural principle that produced state-of-the-art results in vision, language, and reinforcement learning.",
  },
  {
    q: "How do the engines communicate with each other?",
    a: "The engines exchange state through a typed orchestration bus with versioned schemas. Each engine exposes a structured input/output contract, enabling runtime recomposition based on the strategic posture — stealth, moderate, aggressive, or maximum.",
  },
  {
    q: "What is the 144-dimensional Cognitive Resonance Engine?",
    a: "Cognitive Resonance maps audience emotional and cognitive states across 144 dimensions — including emotional tone, ideological alignment, identity markers, narrative preference, and temporal sensitivity. This is 14× deeper than the 5–10 dimensions tracked by conventional sentiment platforms.",
  },
  {
    q: "How does the Organic Simulation Engine work?",
    a: "OSE runs agent-based propagation models with 10,000 to 1,000,000+ concurrent simulated personas. Each persona is calibrated to specific cultural, demographic, and psychographic attributes, and propagates across scale-free, small-world, and random network topologies. The engine predicts cascade probability before deployment.",
  },
  {
    q: "What does the Autonomous Operations Engine orchestrate?",
    a: "AOE closes the loop across all four other engines plus the platform's analytics, content factory, persona networks, and adaptive response systems. It ingests perception signals, triggers simulations, commissions content, deploys narratives, and reports outcomes — without human intervention for sub-critical decisions.",
  },
  {
    q: "Can the engines run independently of each other?",
    a: "Yes. Each engine is deployable as a standalone capability for clients with narrow operational requirements. The composition pattern is additive — you can engage only Echo Chamber Architecture, only Cognitive Resonance, or any subset, and add the others as your operational posture evolves.",
  },
  {
    q: "Are the engines available via API?",
    a: "Yes. Each engine exposes a documented RESTful API surface with webhooks for real-time events. SDKs are available in TypeScript, Python, and Go. Enterprise clients can wire engines directly into existing analytics, marketing, or content pipelines.",
  },
  {
    q: "How long does it take to deploy the engines?",
    a: "Single-engine deployments reach operational status in 2–4 weeks. Full five-engine orchestration reaches operational status in 4–6 weeks. Crisis-class deployments — single engine, narrow scope — can be live in under 60 minutes from strategic sign-off.",
  },
  {
    q: "Is the engine stack secure?",
    a: "Security is foundational, not optional. Each engine runs inside an isolated cryptographic boundary with AES-256 encryption at rest, TLS 1.3 in transit, and zero-trust access control. SOC 2 Type II, ISO 27001, and defense-grade protections apply across the entire stack.",
  },
];

export const enginesCta = {
  eyebrow: "Engage the Engines",
  title: "Five engines. One autonomous perception stack.",
  description:
    "Each Perception X2 engine is deployable standalone or as part of the composed stack. Engage the engine that matches your operational posture — or commission the full orchestration for maximum capability.",
  primary: { label: "Request Engine Briefing", href: "/contact" },
  secondary: { label: "View Platform Overview", href: "/platform/overview" },
  trustItems: [
    "5 proprietary engines",
    "1M+ persona capacity",
    "144-D resonance",
    "Sub-hour deployment",
    "Defence-grade security",
  ],
};

/* Engine radar profile — bespoke visual data */
export interface EnginesRadarAxis {
  axis: string;
  penetration: number;
  authenticity: number;
  speed: number;
  coverage: number;
  insight: number;
}

export const enginesRadar: { label: string; color: string; values: EnginesRadarAxis }[] = [
  {
    label: "Organic Simulation",
    color: "var(--color-cyan)",
    values: {
      axis: "Organic Simulation",
      penetration: 88,
      authenticity: 96,
      speed: 78,
      coverage: 99,
      insight: 91,
    },
  },
  {
    label: "Narrative Generation",
    color: "var(--color-purple)",
    values: {
      axis: "Narrative Generation",
      penetration: 96,
      authenticity: 98,
      speed: 95,
      coverage: 86,
      insight: 82,
    },
  },
  {
    label: "Cognitive Resonance",
    color: "var(--color-crimson)",
    values: {
      axis: "Cognitive Resonance",
      penetration: 92,
      authenticity: 90,
      speed: 81,
      coverage: 88,
      insight: 99,
    },
  },
  {
    label: "Echo Chamber",
    color: "var(--color-amber)",
    values: {
      axis: "Echo Chamber",
      penetration: 94,
      authenticity: 97,
      speed: 88,
      coverage: 93,
      insight: 79,
    },
  },
  {
    label: "Autonomous Ops",
    color: "var(--color-emerald)",
    values: {
      axis: "Autonomous Ops",
      penetration: 89,
      authenticity: 92,
      speed: 99,
      coverage: 95,
      insight: 87,
    },
  },
];

/* Engine composition matrix — which engine does what */
export interface EnginesCompositionRow {
  capability: string;
  ose: string;
  nge: string;
  cre: string;
  eca: string;
  aoe: string;
}

export const enginesComposition: EnginesCompositionRow[] = [
  {
    capability: "Audience modeling",
    ose: "Primary",
    nge: "—",
    cre: "Primary",
    eca: "Secondary",
    aoe: "Orchestrates",
  },
  {
    capability: "Content creation",
    ose: "—",
    nge: "Primary",
    cre: "Informs",
    eca: "—",
    aoe: "Orchestrates",
  },
  {
    capability: "Propagation routing",
    ose: "Simulates",
    nge: "—",
    cre: "—",
    eca: "Primary",
    aoe: "Executes",
  },
  {
    capability: "Sentiment analysis",
    ose: "Informs",
    nge: "—",
    cre: "Primary",
    eca: "—",
    aoe: "Reports",
  },
  {
    capability: "Decision triggers",
    ose: "—",
    nge: "—",
    cre: "Signals",
    eca: "—",
    aoe: "Primary",
  },
  {
    capability: "Real-time adjustment",
    ose: "Re-simulates",
    nge: "Regenerates",
    cre: "Re-maps",
    eca: "Re-routes",
    aoe: "Primary",
  },
  {
    capability: "Persona calibration",
    ose: "Primary",
    nge: "Generates voices",
    cre: "Maps preferences",
    eca: "Routes by affinity",
    aoe: "Orchestrates",
  },
  {
    capability: "Authenticity scoring",
    ose: "Provides baseline",
    nge: "Optimizes output",
    cre: "Measures resonance",
    eca: "Verifies flow",
    aoe: "Reports",
  },
];
